import { Link2 } from "lucide-react";
import { useState } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { trpcClient } from "@/lib/trpc";
import { useInvoices } from "@/queries/subscription-queries";
import LoadingSpinner from "../loading-spinner";
import { Button } from "../ui/button";

function formatCurrency(amount: number) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(amount);
}

interface InvoiceItemProps {
    id: string;
    createdAt: Date;
    amount: number;
}

function InvoiceItem({ id, createdAt, amount }: InvoiceItemProps) {
    const [isLoading, setIsLoading] = useState(false);
    const { formatDate } = useDateFormatter();

    const handleGetInvoicePDF = async (transactionId: string) => {
        setIsLoading(true);
        try {
            const invoiceUrl = await trpcClient.subscription.getInvoicePDF.query({ transactionId });
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = invoiceUrl;
            a.download = `invoice-${transactionId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-between items-center">
            <div className="text-neutral-500">
                <Button
                    type="button"
                    className="p-0! cursor-pointer"
                    onClick={() => handleGetInvoicePDF(id)}
                    disabled={isLoading}
                    variant="link"
                >
                    {formatDate(new Date(createdAt))} {isLoading ? <LoadingSpinner /> : <Link2 className="size-3.5" />}
                </Button>
            </div>
            <div className="text-neutral-500">{formatCurrency(amount)}</div>
        </div>
    );
}

export default function SubscriptionInvoices() {
    const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoices();

    return (
        <>
            <div className="text-lg font-medium mt-6 mb-2">Recent invoices</div>
            {isLoadingInvoices ? <LoadingSpinner /> : null}
            {!isLoadingInvoices && (
                <div className="flex flex-col gap-2 border bg-white rounded-lg p-4">
                    {invoicesData?.map((invoice) => (
                        <InvoiceItem
                            key={invoice.id}
                            id={invoice.id}
                            createdAt={invoice.createdAt}
                            amount={invoice.amount}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
